import Modal from '@sdk/components/ui/modal/Modal';
import HorizontalSeparator from '@sdk/components/ui/HorizontalSeparator';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';

import TimesheetViewModalHeader from '@web/timesheets/TimesheetViewModal/components/TimesheetViewModalHeader/TimesheetViewModalHeader';
import {
  ModalWrapper,
  Content,
} from '@web/timesheets/TimesheetViewModal/timesheetViewModal.styles';
import DateRow from '@web/timeClock/modals/rows/DateRow/DateRow';
import LocationRow from '@web/timeClock/modals/rows/LocationRow/LocationRow';
import PositionRow from '@web/timeClock/modals/rows/PositionRow/PositionRow';
import ScheduledRow from '@web/timeClock/modals/rows/ScheduledRow/ScheduledRow';
import ActualRow from '@web/timeClock/modals/rows/ActualRow/ActualRow';
import TimesheetHistory from '@web/timeClock/TimesheetHistory/TimesheetHistory';
import ModalBanner from '@web/timesheets/EmployeePendingConsentTimesheetViewModal/components/ModalBanner/ModalBanner';
import ModalFooter from '@web/timesheets/EmployeePendingConsentTimesheetViewModal/components/ModalFooter/ModalFooter';
import { TimesheetViewProps } from '@web/timesheets/EmployeePendingConsentTimesheetViewModal/employeePendingConsentTimesheetViewModal.types';
import { useEmployeePendingConsentTimesheetViewModal } from '@web/timesheets/EmployeePendingConsentTimesheetViewModal/employeePendingConsentTimesheetViewModal.hooks';
import { useTimesheetHistory } from '@web/timesheets/hooks/useTimesheetHistory';

const EmployeePendingConsentTimesheetViewModal = ({
  isTimesheetPreviewActive,
  shift,
  onClose,
  onApprove,
  onDeny,
}: TimesheetViewProps) => {
  const {
    isVisible,
    modalConfig,
    hasAnyTimesheet,
    clockInTimes,
    clockInTimesBefore,
    handleClose,
    isHistoryView,
    setIsHistoryView,
    approve,
    deny,
  } = useEmployeePendingConsentTimesheetViewModal({
    isTimesheetPreviewActive,
    shift,
    onClose,
    onApprove,
    onDeny,
  });

  const { historyItems, fetchHistory } = useTimesheetHistory(shift.id, shift.user.id);

  const toggleHistoryView = () => {
    if (!isHistoryView) {
      fetchHistory();
    }

    setIsHistoryView(!isHistoryView);
  };

  if (isHistoryView) {
    return (
      <Modal isVisible={isVisible} fixed config={modalConfig}>
        <ModalWrapper>
          <TimesheetViewModalHeader
            onClose={handleClose}
            shift={shift}
            toggleHistory={toggleHistoryView}
            isHistoryView={isHistoryView}
            themeColor={ThemeColorType.primary}
            showHistoryButton={Boolean(historyItems?.length)}
          />
          <TimesheetHistory historyItems={historyItems} back={toggleHistoryView} />
        </ModalWrapper>
      </Modal>
    );
  }

  return (
    <Modal isVisible={isVisible} fixed config={modalConfig}>
      <ModalWrapper>
        <TimesheetViewModalHeader
          onClose={handleClose}
          shift={shift}
          toggleHistory={toggleHistoryView}
          isHistoryView={isHistoryView}
          themeColor={ThemeColorType.warning}
          showHistoryButton={Boolean(historyItems?.length)}
        />
        <ModalBanner
          managerName={shift.lastEditedByName}
          status={shift.timesheetStatus}
          isManagerView={false}
          isAddition={!shift.timesheetEntriesBefore?.length}
        />
        <Content>
          <DateRow date={shift.dtstart} />
          <HorizontalSeparator themeColor={ThemeColorType.separatorOnGray} />
          <LocationRow location={shift.location} />
          <HorizontalSeparator themeColor={ThemeColorType.separatorOnGray} />
          <PositionRow position={shift.position} />
          <HorizontalSeparator themeColor={ThemeColorType.separatorOnGray} />
          <ScheduledRow shift={shift} />
          <HorizontalSeparator themeColor={ThemeColorType.separatorOnGray} />
          {hasAnyTimesheet && (
            <>
              <ActualRow
                shift={shift}
                clockInTimes={clockInTimes}
                clockInTimesBefore={clockInTimesBefore}
              />
              <HorizontalSeparator themeColor={ThemeColorType.separatorOnGray} />
            </>
          )}
        </Content>
        <ModalFooter
          deny={deny}
          approve={approve}
          close={handleClose}
          status={shift.timesheetStatus}
        />
      </ModalWrapper>
    </Modal>
  );
};

export default EmployeePendingConsentTimesheetViewModal;
